import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import LanguageSelector from './components/LanguageSelector.jsx';
import PartnerHero from './components/PartnerHero.jsx';
import PartnerLogin from './components/PartnerLogin.jsx';
import EnhancedPartnerDashboard from './components/EnhancedPartnerDashboard.jsx';
import PartnerProgramDetail from './components/PartnerProgramDetail.jsx';
import LearnMoreCombined from './components/LearnMoreCombined.jsx';
import LearnMoreSimple from './components/LearnMoreSimple';
import SimplePartnerForm from './SimplePartnerForm.jsx';
import SiPunojmeNe from './components/SiPunojmeNe.jsx';
import BookingStatus from './components/BookingStatus.jsx';
import ReviewForm from './components/ReviewForm.jsx';
import Privacy from './components/Privacy.jsx';
import Imprint from './components/Imprint.jsx';
import Footer from './components/Footer.jsx';
import InstallPWA from './components/InstallPWA.jsx';
import './App.css';

// Import essential components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { toast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';

import { 
  CalendarIcon,
  Sparkles, 
  UserPlus, 
  Award, 
  CheckCircle, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle,
  Star, 
  Users, 
  Shield, 
  Quote, 
  ClipboardList, 
  HomeIcon, 
  Droplets, 
  Wrench, 
  Leaf, 
  ArrowRight, 
  PhoneCall, 
  Briefcase, 
  DollarSign, 
  TrendingUp, 
  UserCheck,
  BookOpen
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service data with hourly rate calculation (with 20% commission included)
// All languages use Swiss pricing as reference
const HOURLY_RATE = 60; // 60 CHF pro Stunde
const SWISS_HOURLY_RATE = 60; // 60 CHF pro Stunde

const calculateServicePrice = (duration, serviceId = null, language = 'de') => {
  // Calculate price based on work hours (commission already included)
  // All languages use Swiss pricing as reference: 90 CHF per hour
  if (typeof duration === 'number') {
    let basePrice;
    
    // All markets use same hourly rate: 90 CHF
    basePrice = duration * HOURLY_RATE; // Hours × 60 CHF
    
    // Special case: "Deep Cleaning for Hotels" is B2B and gets 12% total discount
    if (serviceId === 'deep_cleaning') {
      basePrice = basePrice * 0.88; // 12% B2B discount = 88% of original price
    }
    
    return Math.round(basePrice); // Round to whole units
  }
  return null; // For variable services
};

const getServicePrice = (service, language) => {
  // All languages use Swiss pricing as reference
  const calculatedPrice = calculateServicePrice(service.duration, service.id, language);
  if (calculatedPrice) {
    return calculatedPrice;
  } else {
    // For services with variable duration
    if (language === 'de-ch') return 'Uf Afrag';
    if (language === 'de') return 'Auf Anfrage';
    if (language === 'it') return 'Su Richiesta';
    if (language === 'fr') return 'Sur Demande';
    if (language === 'en') return 'On Request';
    return 'Auf Anfrage';
  }
};

// Backend-Kalkulationspreise entfernt - Preise basieren auf Arbeitsstunden

const getServiceCurrency = (language) => {
  // All languages use CHF as currency (Swiss pricing as reference)
  return 'CHF';
};

// Function to convert numeric duration to written format in different languages
const getDurationText = (duration, language) => {
  if (typeof duration !== 'number') {
    // For variable durations
    if (language === 'de-ch') return 'Variable Duur';
    if (language === 'de') return 'Variable Dauer';
    if (language === 'en') return 'Variable Duration';
    if (language === 'it') return 'Durata Variabile';
    if (language === 'fr') return 'Durée Variable';
    return 'Variable Duration';
  }

  const hours = duration;
  
  if (language === 'de-ch') { // Swiss German
    if (hours === 1) return 'ei Stundä';
    if (hours === 1.5) return 'eineinhalb Stundä';
    if (hours === 2) return 'zwöi Stundä';
    if (hours === 3) return 'drü Stundä';
    if (hours === 6) return 'sächs Stundä';
    if (hours === 8) return 'acht Stundä';
    if (hours === 24) return 'vieruzwänzg Stundä';
    if (hours === 40) return 'vierzg Stundä';
    return `${hours} Stundä`;
  }
  
  if (language === 'de') { // German
    if (hours === 1) return 'eine Stunde';
    if (hours === 1.5) return 'eineinhalb Stunden';
    if (hours === 2) return 'zwei Stunden';
    if (hours === 3) return 'drei Stunden';
    if (hours === 6) return 'sechs Stunden';
    if (hours === 8) return 'acht Stunden';
    if (hours === 24) return 'vierundzwanzig Stunden';
    if (hours === 40) return 'vierzig Stunden';
    return `${hours} Stunden`;
  }
  
  if (language === 'en') { // English
    if (hours === 1) return 'one hour';
    if (hours === 1.5) return 'one and a half hours';
    if (hours === 2) return 'two hours';
    if (hours === 3) return 'three hours';
    if (hours === 6) return 'six hours';
    if (hours === 8) return 'eight hours';
    if (hours === 24) return 'twenty-four hours';
    if (hours === 40) return 'forty hours';
    return `${hours} hours`;
  }
  
  if (language === 'it') { // Italian
    if (hours === 1) return 'un\'ora';
    if (hours === 1.5) return 'un\'ora e mezza';
    if (hours === 2) return 'due ore';
    if (hours === 3) return 'tre ore';
    if (hours === 6) return 'sei ore';
    if (hours === 8) return 'otto ore';
    if (hours === 24) return 'ventiquattro ore';
    if (hours === 40) return 'quaranta ore';
    return `${hours} ore`;
  }
  
  if (language === 'fr') { // French
    if (hours === 1) return 'une heure';
    if (hours === 1.5) return 'une heure et demie';
    if (hours === 2) return 'deux heures';
    if (hours === 3) return 'trois heures';
    if (hours === 6) return 'six heures';
    if (hours === 8) return 'huit heures';
    if (hours === 24) return 'vingt-quatre heures';
    if (hours === 40) return 'quarante heures';
    return `${hours} heures`;
  }
  
  // Default fallback (German)
  return `${hours} Stunden`;
};

const services = [
  {
    id: 'general_cleaning',
    name: 'Pastrimi i Përgjithshëm i Shtëpisë',
    description: 'Shërbim i plotë pastrimi për shtëpi dhe apartamente',
    price: 290,
    duration: 24,
    features: ['Fshirja e pluhurit', 'Fshesa me korrent', 'Larja e dyshemesë', 'Pastrimi i banjës', 'Pastrimi i kuzhinës'],
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952'
  },
  {
    id: 'apartment_cleaning',
    name: 'Pastrimi i Përgjithshëm i Apartmentevë',
    description: 'Shërbim i plotë pastrimi për shtëpi dhe apartamente',
    price: 45,
    duration: 3,
    features: ['Fshirja e pluhurit', 'Fshesa me korrent', 'Larja e dyshemesë', 'Pastrimi i banjës', 'Pastrimi i kuzhinës'],
    image: 'https://images.unsplash.com/photo-1631365696563-4990f4e9302c'
  },
  {
    id: 'business_cleaning',
    name: 'Pastrimi i Bizneseve dhe Zyrave',
    description: 'Pastrimi profesional për zyra dhe hapësira biznesi',
    price: 300,
    duration: 5,
    features: ['Hapësirat e zyrave', 'Sallat e mbledhjeve', 'Hapësirat e përbashkëta', 'Dezinfektimi', 'Menaxhimi i mbeturinave'],
    image: 'https://images.unsplash.com/photo-1664008760004-182420e58e7c'
  },
  {
    id: 'airbnb_management',
    name: 'Menaxhimi i AirBNB dhe Shërbimet e Çelësave',
    description: 'Shërbim i plotë ndërrimi i AirBNB me menaxhim çelësash',
    price: 25,
    duration: 1.5,
    features: ['Koordinimi i check-in/out', 'Dorëzimi i çelësave', 'Kontrolli i inventarit', 'Pastrimi', 'Mirëmbajtja'],
    image: 'https://images.unsplash.com/photo-1604367159782-5ba0ba6ac604'
  },
  {
    id: 'building_maintenance',
    name: 'Pastrimi i Mirëmbajtjes për Ndërtesa Shumëfamiljare',
    description: 'Shërbim profesional mirëmbajtjeje për ndërtesa të mëdha rezidenciale',
    price: 'Me kërkesë',
    duration: 'E ndryshueshme',
    features: ['Pastrimi i korridoreve', 'Mirëmbajtja e shkallëve', 'Pastrimi i dritareve', 'Zonët e përbashkëta', 'Pastrimi i jashtëm'],
    image: 'https://images.unsplash.com/photo-1421940943431-d392fcc1079f'
  },
  {
    id: 'rental_management',
    name: 'Menaxhimi i Qirave dhe Pronave',
    description: 'Shërbim i plotë menaxhimi për prona me qira',
    price: 'Me kërkesë',
    duration: 'E ndryshueshme',
    features: ['Koordinimi i qirave', 'Inspektimi i pronës', 'Menaxhimi i çelësave', 'Koordinimi i mirëmbajtjes', 'Raportimi i dëmeve'],
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa'
  },
  {
    id: 'maintenance_cleaning',
    name: 'Unterhaltsreinigung',
    description: 'Regelmässige Unterhaltsreinigung für Wohnungen',
    price: 'Me kërkesë',
    duration: 'E ndryshueshme',
    features: ['Regelmässige Reinigung', 'Flexible Termine', 'Professionelles Team'],
    image: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac'
  }
];

// Hero Component
const Hero = ({ onBookNow, onLearnMore, onPartnerProgram, onPartnerLogin }) => {
  const { t, language } = useLanguage();
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-teal-50"></div>
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1742483359033-13315b247c74')] bg-cover bg-center opacity-10"></div>
      
      <div className="relative z-10 container mx-auto px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <Badge className="mb-6 bg-emerald-100 text-emerald-800 border-emerald-200 px-4 py-2 text-sm font-medium">
            {t('services.subtitle', 'Shërbime Profesionale Pastrimi në Prizren')}
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-2 leading-tight">
            <span className="bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
              Brillanz & Glanz
            </span>
          </h1>
          
          <p className="text-lg md:text-2xl font-semibold text-emerald-700 mb-6">
            {t('hero.slogan', 'Leben mit Exzellenz')}
          </p>
          
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('hero.subtitle', 'Shërbime premium pastrimi për shtëpi, zyra, prona AirBNB, Restaurante dhe hotele. Duke krijuar rend, pastërti dhe cilësi të përmirësuar jetese në Prizren dhe më gjerë.')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-lg font-semibold rounded-lg shadow-lg transition-all duration-300"
              onClick={onBookNow}
            >
              {t('hero.bookNow', 'Rezervo Tani')}
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white px-8 py-3 text-lg font-semibold rounded-lg transition-all duration-300"
              onClick={onLearnMore}
            >
              {t('hero.learnMore', 'Mëso Më Shumë')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm rounded-lg p-3 shadow-md">
              <Shield className="h-6 w-6 text-emerald-600" />
              <span className="font-medium text-gray-800">{t('trust.secure', 'I Besueshëm dhe i Sigurt')}</span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm rounded-lg p-3 shadow-md">
              <Users className="h-6 w-6 text-emerald-600" />
              <span className="font-medium text-gray-800">{t('trust.experienced', 'Ekip me Përvojë')}</span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm rounded-lg p-3 shadow-md">
              <Star className="h-6 w-6 text-emerald-600" />
              <span className="font-medium text-gray-800">{t('trust.fivestar', 'Shërbim 5-Yje')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Component
const Services = ({ onServiceSelect, onBookNow }) => {
  const { t, language } = useLanguage();
  
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('services.title', 'Shërbimet Tona')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            {t('services.description', 'Zgjidhje profesionale pastrimi të përshtatura për nevojat tuaja. Nga residencial në komercial, ne ofrojmë shërbim të jashtëzakonshëm me produkte miqësore me mjedisin.')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.slice(0, 7).map((service) => {
            const calculatedPrice = getServicePrice(service, language);
            const currency = getServiceCurrency(language);
            
            // Get translated service name and description - fix key mapping
            let serviceKey = service.id;
            if (service.id === 'general_cleaning') serviceKey = 'general';
            if (service.id === 'apartment_cleaning') serviceKey = 'apartment';
            if (service.id === 'business_cleaning') serviceKey = 'business';
            if (service.id === 'deep_cleaning') serviceKey = 'deep';
            if (service.id === 'airbnb_management') serviceKey = 'airbnb';
            if (service.id === 'facade_cleaning') serviceKey = 'facade';
            if (service.id === 'building_maintenance') serviceKey = 'building';
            if (service.id === 'park_maintenance') serviceKey = 'park';
            
            const serviceName = t(`service.${serviceKey}.name`, service.name);
            const serviceDesc = t(`service.${serviceKey}.desc`, service.description);
            
            return (
              <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg overflow-hidden">
                <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={serviceName}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">
                    {serviceName}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {serviceDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-emerald-600">
                      {typeof calculatedPrice === 'string' ? calculatedPrice : `${currency}${calculatedPrice}`}
                    </span>
                    <div className="flex items-center text-gray-500">
                      <Clock className="h-4 w-4 mr-1" />
                      <span className="text-sm">{getDurationText(service.duration, language)}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 2).map((feature, index) => {
                      // Map Albanian feature names to translation keys
                      let featureKey = '';
                      if (feature === 'Fshirja e pluhurit') featureKey = 'feature.dusting';
                      else if (feature === 'Fshesa me korrent') featureKey = 'feature.vacuum';
                      else if (feature === 'Larja e dyshemesë') featureKey = 'feature.mopping';
                      else if (feature === 'Pastrimi i banjës') featureKey = 'feature.bathroom';
                      else if (feature === 'Pastrimi i kuzhinës') featureKey = 'feature.kitchen';
                      else if (feature === 'Hapësirat e zyrave') featureKey = 'feature.offices';
                      else if (feature === 'Sallat e mbledhjeve') featureKey = 'feature.meetings';
                      else if (feature === 'Hapësirat e përbashkëta') featureKey = 'feature.common';
                      else if (feature === 'Dezinfektimi') featureKey = 'feature.disinfection';
                      else if (feature === 'Menaxhimi i mbeturinave') featureKey = 'feature.waste';
                      else if (feature === 'Koordinimi i check-in/out') featureKey = 'feature.checkin';
                      else if (feature === 'Dorëzimi i çelësave') featureKey = 'feature.keys';
                      else if (feature === 'Kontrolli i inventarit') featureKey = 'feature.inventory';
                      else if (feature === 'Pastrimi') featureKey = 'feature.cleaning';
                      else if (feature === 'Mirëmbajtja') featureKey = 'feature.maintenance';
                      else if (feature === 'Pastrimi i fasadave') featureKey = 'feature.facades';
                      else if (feature === 'Heqja e graffiti-ve') featureKey = 'feature.graffiti';
                      else if (feature === 'Pastrimi me presion') featureKey = 'feature.pressure';
                      else if (feature === 'Restaurimi i fasadave') featureKey = 'feature.restoration';
                      else if (feature === 'Mbrojtja dhe konservimi') featureKey = 'feature.protection';
                      else if (feature === 'Pastrimi i korridoreve') featureKey = 'feature.corridors';
                      else if (feature === 'Mirëmbajtja e shkallëve') featureKey = 'feature.stairs';
                      else if (feature === 'Pastrimi i dritareve') featureKey = 'feature.windows';
                      else if (feature === 'Zonët e përbashkëta') featureKey = 'feature.commonareas';
                      else if (feature === 'Pastrimi i jashtëm') featureKey = 'feature.external';
                      else if (feature === 'Kujdesi për barishtë') featureKey = 'feature.grass';
                      else if (feature === 'Prerja e drunjve') featureKey = 'feature.trees';
                      else if (feature === 'Pastrimi i parqeve') featureKey = 'feature.parks';
                      else if (feature === 'Mirëmbajtja e lulishteve') featureKey = 'feature.gardens';
                      else if (feature === 'Sistemi i ujitjes') featureKey = 'feature.irrigation';
                      else featureKey = 'feature.cleaning'; // fallback
                      
                      // Get translated feature text
                      const translatedFeature = t(featureKey, feature);
                      return (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 flex-shrink-0" />
                          {translatedFeature}
                        </li>
                      );
                    })}
                  </ul>
                  
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-lg transition-all duration-300"
                    onClick={() => onServiceSelect({
                      ...service, 
                      calculatedPrice: calculatedPrice, 
                      currency,
                      name: serviceName, 
                      description: serviceDesc
                    })}
                  >
                    {t('button.selectService', 'Zgjidh Shërbimin')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* CTA after services */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 px-2">
              {t('cta.notfound', 'Nuk po gjeni shërbimin që kërkoni?')}
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 px-2">
              {t('cta.contact', 'Na kontaktoni për një ofertë të personalizuar. Jemi këtu për t\'ju ndihmuar!')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-2">
              <a 
                href={`https://wa.me/+41767669430?text=${encodeURIComponent(t('whatsapp.quoteMessage', 'Hallo! Ich interessiere mich für Ihre Reinigungsdienstleistungen und möchte ein kostenloses Angebot erhalten.'))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-4 sm:px-8 text-sm sm:text-base md:text-lg rounded-full shadow-lg transition-colors duration-200 no-underline"
              >
                <PhoneCall className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                {t('button.requestQuote', 'Kërko Ofertë Falas')}
              </a>
              
              <a
                href={`https://wa.me/+41767669430?text=${encodeURIComponent(t('whatsapp.contactMessage', 'Hallo! Ich möchte mehr über Ihre Reinigungsdienstleistungen erfahren.'))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 py-3 px-4 sm:px-8 text-sm sm:text-base md:text-lg rounded-full transition-colors duration-200 no-underline"
              >
                <Mail className="mr-2 h-5 w-5" />
                {t('button.whatsapp', 'WhatsApp')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Partner Program Section Component (now separate from Hero)
const PartnerProgramSection = ({ onPartnerProgram, onPartnerLogin }) => {
  const { t, language } = useLanguage();
  
  const getPartnerText = () => {
    if (language === 'de-ch') {
      return 'Schliesse di üsem Team aa und verdien bis zu CHF 5500/Monet als professionelle Mitarbeiter';
    }
    if (language === 'en') {
      return 'Join our team and earn competitive rates as a professional collaborator';
    }
    return 'Bashkohuni me ekipin tonë dhe fitoni deri në €650/muaj si Bashkepuntor profesional';
  };

  const getPartnerButtons = () => {
    return {
      discover: t('partner.program.discover', 'Zbulo Programin Partner'),
      login: t('partner.program.login', 'Kyçuni si Partner')
    };
  };

  const buttons = getPartnerButtons();
  
  return (
    <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="flex items-center justify-center mb-8">
            <UserPlus className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-blue-400 mr-2 sm:mr-4" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
              {language === 'de-ch' ? 'Partner Programm' : 
               language === 'en' ? 'Partner Program' : 
               'Programi Partner'}
            </h2>
          </div>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-12 max-w-3xl mx-auto px-2">
            {getPartnerText()}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center mb-12 px-4">
            <Button 
              size="lg" 
              onClick={onPartnerProgram}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 sm:py-4 px-4 sm:px-8 text-sm sm:text-base md:text-lg rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <UserPlus className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6" />
              {buttons.discover}
            </Button>
            
            <Button 
              variant="outline" 
              size="lg"
              onClick={onPartnerLogin}
              className="border-white text-white hover:bg-white/10 backdrop-blur-sm py-3 sm:py-4 px-4 sm:px-8 text-sm sm:text-base md:text-lg rounded-full"
            >
              <Award className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6" />
              {buttons.login}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Google Reviews Component
const GoogleReviews = () => {
  const reviews = [
    {
      id: 1,
      name: "Fatmir Krasniqi",
      rating: 5,
      date: "Para 2 javësh",
      comment: "Shërbim i shkëlqyer! Ekipi i Brillanz & Glanz e pastroi shtëpinë tonë në mënyrë profesionale. Shumë të kënaqur me rezultatin.",
      service: "Pastrimi i Shtëpisë"
    },
    {
      id: 2,
      name: "Blerina Hoti",
      rating: 5,
      date: "Para 1 muaji",
      comment: "Autodetailing fantastik! Makina ime duket si e re. Çmimi i arsyeshëm dhe cilësi e lartë. Do t'i rekomandoj të gjithëve!",
      service: "Autodetailing"
    },
    {
      id: 3,
      name: "Driton Selimi",
      rating: 5,
      date: "Para 3 javësh",
      comment: "Pastrimi i zyrës u bë në kohë dhe me cilësi të lartë. Stafi shumë profesional dhe i sjellshëm. Faleminderit!",
      service: "Pastrimi i Zyrës"
    },
    {
      id: 4,
      name: "Arjeta Muhaxhiri",
      rating: 5,
      date: "Para 1 jave",
      comment: "Shërbim i jashtëzakonshëm për AirBNB! Gjithçka e organizuar shumë mirë. Menaxhimi i çelësave perfekt.",
      service: "AirBNB Management"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Google" className="h-8" />
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
              ))}
              <span className="ml-2 text-lg font-semibold text-gray-700">4.9 nga 5</span>
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 px-2">Çfarë Thonë Klientët Tanë</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Më shumë se 200+ klientë të kënaqur në Prizren dhe rrethinë
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.map((review) => (
            <Card key={review.id} className="p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">{review.name}</h4>
                  <p className="text-sm text-gray-500">{review.date}</p>
                </div>
                <div className="flex items-center">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>
              <Quote className="h-8 w-8 text-emerald-200 mb-3" />
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">{review.comment}</p>
              <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                {review.service}
              </Badge>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Booking Form Wrapper with Language Key
const BookingFormWrapper = ({ selectedService, onBack }) => {
  const { language } = useLanguage();
  
  return (
    <BookingForm 
      key={`booking-${language}`}
      selectedService={selectedService} 
      onBack={onBack}
    />
  );
};

// Booking Form Component
const BookingForm = ({ selectedService, onBack }) => {
  const { t, language } = useLanguage();
  
  // Debug: Log current language
  console.log('BookingForm render with language:', language);
  
  const [currentService, setCurrentService] = useState(selectedService);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address: '',
    preferred_date: null,
    preferred_time: '',
    special_requirements: '',
    payment_method: 'card'
  });
  const [loading, setLoading] = useState(false);


  
  // Hourly rates per language/market and service
  // All languages use Swiss pricing as reference: 60 CHF per hour
  const hourlyRates = {
    'de': { // German market (CHF-based, using Swiss pricing)
      'general_cleaning': 60,
      'apartment_cleaning': 60,
      'airbnb_management': 60,
      'business_cleaning': 60,
      'deep_cleaning': 52.8, // 60 * 0.88 (12% B2B discount)
      'building_maintenance': 60,
      'maintenance_cleaning': 60
    },
    'de-ch': { // Swiss German market (CHF-based)
      'general_cleaning': 60,
      'apartment_cleaning': 60,
      'airbnb_management': 60,
      'business_cleaning': 60,
      'deep_cleaning': 52.8, // 60 * 0.88 (12% B2B discount)
      'building_maintenance': 60,
      'maintenance_cleaning': 60
    },
    'en': { // English market (CHF-based, using Swiss pricing)
      'general_cleaning': 60,
      'apartment_cleaning': 60,
      'airbnb_management': 60,
      'business_cleaning': 60,
      'deep_cleaning': 52.8, // 60 * 0.88 (12% B2B discount)
      'building_maintenance': 60,
      'maintenance_cleaning': 60
    },
    'it': { // Italian market (CHF-based, using Swiss pricing)
      'general_cleaning': 60,
      'apartment_cleaning': 60,
      'airbnb_management': 60,
      'business_cleaning': 60,
      'deep_cleaning': 52.8, // 60 * 0.88 (12% B2B discount)
      'building_maintenance': 60,
      'maintenance_cleaning': 60
    },
    'fr': { // French market (CHF-based, using Swiss pricing)
      'general_cleaning': 60,
      'apartment_cleaning': 60,
      'airbnb_management': 60,
      'business_cleaning': 60,
      'deep_cleaning': 52.8, // 60 * 0.88 (12% B2B discount)
      'building_maintenance': 60,
      'maintenance_cleaning': 60
    }
  };
  
  // Get language-based pricing calculation (with 20% commission included)
  // All languages use Swiss pricing as reference
  const getMarketPricing = (serviceId, duration) => {
    const rate = hourlyRates[language]?.[serviceId];
    
    // If rate is not defined or duration is null/undefined/not a number, show "On Request"
    if (!rate || !duration || typeof duration !== 'number') {
      const onRequestTexts = {
        'de': 'Auf Anfrage',
        'de-ch': 'Uf Afrag',
        'en': 'On Request',
        'it': 'Su Richiesta',
        'fr': 'Sur Demande'
      };
      return { 
        price: onRequestTexts[language] || onRequestTexts['de'], 
        currency: '' 
      };
    }
    
    // Calculate: Hours x Hourly Rate (commission already included in rates)
    const totalPrice = Math.round(duration * rate);
    
    // All languages use CHF (Swiss pricing as reference)
    const currency = 'CHF';
    
    return { 
      price: totalPrice, 
      currency: currency 
    };
  };

  // Define services with proper translations and market pricing - MEMOIZED
  const getLocalizedServices = useCallback(() => {
    // Base services with IDs and images only, multilingual features defined separately
    const baseServices = [
      {
        id: 'general_cleaning',
        image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop&crop=center',
        basePrice: 25,
        duration: 24
      },
      {
        id: 'apartment_cleaning',
        image: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300&fit=crop&crop=center',
        basePrice: 30,
        duration: 3
      },
      {
        id: 'airbnb_management',
        image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&h=300&fit=crop&crop=center',
        basePrice: 35,
        duration: 1.5
      },
      {
        id: 'business_cleaning',
        image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop&crop=center', 
        basePrice: 20,
        duration: 5
      },
      {
        id: 'building_maintenance',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop&crop=center',
        basePrice: 60,
        duration: null // Variable duration - shows "Upon Request"
      },
      {
        id: 'rental_management',
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop&crop=center',
        basePrice: 0,
        duration: null // Variable duration - shows "Upon Request"
      },
      {
        id: 'maintenance_cleaning',
        image: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=400&h=300&fit=crop&crop=center',
        basePrice: 0,
        duration: null // Variable duration - shows "Auf Anfrage"
      }
    ];

    // Multilingual features for each service
    const serviceFeatures = {
      'general_cleaning': {
        de: ['Staub wischen', 'Staubsaugen', 'Böden wischen', 'Bad reinigen', 'Küche reinigen', 'Möbel reinigen', 'Zimmer aufräumen'],
        'de-ch': ['Staub wische', 'Staubsuge', 'Bode wische', 'Bad putze', 'Chuchi putze', 'Möbel reinigie', 'Zimer ufröime'],
        en: ['Dust removal', 'Vacuuming', 'Floor mopping', 'Bathroom cleaning', 'Kitchen cleaning', 'Furniture cleaning', 'Room organization'],
        it: ['Spolveratura', 'Aspirapolvere', 'Lavaggio pavimenti', 'Pulizia bagno', 'Pulizia cucina', 'Pulizia mobili', 'Organizzazione stanze'],
        fr: ['Dépoussiérage', 'Aspiration', 'Lavage des sols', 'Nettoyage salle de bain', 'Nettoyage cuisine', 'Nettoyage meubles', 'Organisation des pièces']
      },
      'apartment_cleaning': {
        de: ['Balkon reinigen', 'Staub wischen', 'Böden wischen', 'Bad reinigen', 'Küche reinigen', 'Fenster putzen', 'Oberflächen desinfizieren'],
        'de-ch': ['Balkon putze', 'Staub wische', 'Bode wische', 'Bad putze', 'Chuchi putze', 'Fänschter putze', 'Oberfläche desinfiziere'],
        en: ['Balcony cleaning', 'Dust removal', 'Floor mopping', 'Bathroom cleaning', 'Kitchen cleaning', 'Window cleaning', 'Surface disinfection'],
        it: ['Pulizia balcone', 'Spolveratura', 'Lavaggio pavimenti', 'Pulizia bagno', 'Pulizia cucina', 'Pulizia finestre', 'Disinfezione superfici'],
        fr: ['Nettoyage balcon', 'Dépoussiérage', 'Lavage des sols', 'Nettoyage salle de bain', 'Nettoyage cuisine', 'Nettoyage fenêtres', 'Désinfection surfaces']
      },
      'airbnb_management': {
        de: ['Check-in/out koordinieren', 'Schlüsselübergabe', 'Inventar kontrollieren', 'Reinigung', 'Wartung', 'Schäden melden', 'Bettwäsche wechseln'],
        'de-ch': ['Check-in/out koordiniere', 'Schlüsse übergä', 'Inventar kontrolle', 'Putze', 'Wartung', 'Schäde melde', 'Bettzüüg wächsle'],
        en: ['Check-in/out coordination', 'Key handover', 'Inventory control', 'Cleaning', 'Maintenance', 'Damage reporting', 'Linen change'],
        it: ['Coordinamento check-in/out', 'Consegna chiavi', 'Controllo inventario', 'Pulizia', 'Manutenzione', 'Segnalazione danni', 'Cambio biancheria'],
        fr: ['Coordination check-in/out', 'Remise des clés', 'Contrôle inventaire', 'Nettoyage', 'Entretien', 'Rapport de dégâts', 'Changement du linge']
      },
      'business_cleaning': {
        de: ['Büroräume', 'Besprechungsräume', 'Gemeinschaftsräume', 'Desinfektion', 'Abfallmanagement', 'Tische reinigen', 'Technikgeräte reinigen'],
        'de-ch': ['Büroröim', 'Sitzigszimer', 'Gmeinschaftsröim', 'Desinfiziere', 'Abfall-Management', 'Tisch putze', 'Technik-Gerät reinigie'],
        en: ['Office spaces', 'Meeting rooms', 'Common areas', 'Disinfection', 'Waste management', 'Table cleaning', 'Equipment cleaning'],
        it: ['Spazi uffici', 'Sale riunioni', 'Aree comuni', 'Disinfezione', 'Gestione rifiuti', 'Pulizia tavoli', 'Pulizia attrezzature'],
        fr: ['Espaces de bureaux', 'Salles de réunion', 'Espaces communs', 'Désinfection', 'Gestion des déchets', 'Nettoyage tables', 'Nettoyage équipements']
      },
      'deep_cleaning': {
        de: ['Tiefe Desinfektion', 'Teppichreinigung', 'Fenster putzen', 'Desinfektion', 'Premium-Service', 'Duschen reinigen', 'Hotelmöbel reinigen'],
        'de-ch': ['Tiifi Desinfekzion', 'Teppich reinigie', 'Fänschter putze', 'Desinfiziere', 'Premium-Service', 'Dusche putze', 'Hotel-Möbel reinigie'],
        en: ['Deep disinfection', 'Carpet cleaning', 'Window cleaning', 'Disinfection', 'Premium service', 'Shower cleaning', 'Hotel furniture cleaning'],
        it: ['Disinfezione profonda', 'Pulizia tappeti', 'Pulizia finestre', 'Disinfezione', 'Servizio premium', 'Pulizia docce', 'Pulizia mobili hotel'],
        fr: ['Désinfection profonde', 'Nettoyage tapis', 'Nettoyage fenêtres', 'Désinfection', 'Service premium', 'Nettoyage douches', 'Nettoyage mobilier hôtel']
      },
      'building_maintenance': {
        de: ['Flur reinigen', 'Treppenhaus-Wartung', 'Fenster putzen', 'Gemeinschaftsbereiche', 'Außenreinigung', 'System-Kontrolle', 'Lift-Wartung'],
        'de-ch': ['Gang putze', 'Stiägehüüs-Wartung', 'Fänschter putze', 'Gmeinschaftsbereich', 'Ussereinigiung', 'System-Kontrolle', 'Lift-Wartung'],
        en: ['Hallway cleaning', 'Staircase maintenance', 'Window cleaning', 'Common areas', 'Exterior cleaning', 'System control', 'Elevator maintenance'],
        it: ['Pulizia corridoi', 'Manutenzione scale', 'Pulizia finestre', 'Aree comuni', 'Pulizia esterna', 'Controllo sistemi', 'Manutenzione ascensore'],
        fr: ['Nettoyage couloirs', 'Entretien escaliers', 'Nettoyage fenêtres', 'Espaces communs', 'Nettoyage extérieur', 'Contrôle systèmes', 'Entretien ascenseur']
      },
      'rental_management': {
        de: ['Mietersuche', 'Vertragsabwicklung', 'Besichtigungen', 'Mieterverwaltung', 'Dokumentation', 'Übergabeprotokoll'],
        'de-ch': ['Mietersuech', 'Vertragsabwicklig', 'Besichtigunge', 'Mieterverwaltig', 'Dokumentation', 'Übergabprotokoll'],
        en: ['Tenant search', 'Contract handling', 'Property viewings', 'Tenant management', 'Documentation', 'Handover protocol'],
        it: ['Ricerca inquilini', 'Gestione contratti', 'Visite immobili', 'Gestione inquilini', 'Documentazione', 'Protocollo consegna'],
        fr: ['Recherche locataires', 'Gestion contrats', 'Visites propriétés', 'Gestion locataires', 'Documentation', 'Protocole remise']
      },
      'park_maintenance': {
        de: ['Rasenpflege', 'Baumschnitt', 'Heckenschneiden', 'Blumenpflege', 'Unkrautentfernung', 'Bewässerung', 'Laubentfernung'],
        'de-ch': ['Rasepfleg', 'Baumschnitt', 'Häckeschnyde', 'Bluemepfleg', 'Unkruut entferne', 'Bewässerig', 'Laub entferne'],
        en: ['Lawn care', 'Tree pruning', 'Hedge trimming', 'Flower care', 'Weed removal', 'Irrigation', 'Leaf removal'],
        it: ['Cura prato', 'Potatura alberi', 'Taglio siepi', 'Cura fiori', 'Rimozione erbacce', 'Irrigazione', 'Rimozione foglie'],
        fr: ['Entretien pelouse', 'Taille arbres', 'Taille haies', 'Soins fleurs', 'Désherbage', 'Irrigation', 'Enlèvement feuilles']
      },
      'painting_services': {
        de: ['Innenanstrich', 'Außenanstrich', 'Tapezieren', 'Fassadenanstrich', 'Lackierarbeiten', 'Renovierung', 'Farbberatung'],
        'de-ch': ['Inne-Aastrich', 'Usse-Aastrich', 'Tapeziere', 'Fassade-Aastrich', 'Lackierarbete', 'Renovierig', 'Farbberaotig'],
        en: ['Interior painting', 'Exterior painting', 'Wallpapering', 'Facade painting', 'Lacquering', 'Renovation', 'Color consultation'],
        it: ['Pittura interni', 'Pittura esterni', 'Tappezzeria', 'Pittura facciate', 'Verniciatura', 'Ristrutturazione', 'Consulenza colori'],
        fr: ['Peinture intérieure', 'Peinture extérieure', 'Tapisserie', 'Peinture façades', 'Laquage', 'Rénovation', 'Conseil couleurs']
      },
      'maintenance_cleaning': {
        de: ['Regelmässige Reinigung', 'Flexible Termine', 'Professionelles Team', 'Qualitätsgarantie', 'Individuelle Anpassung'],
        'de-ch': ['Regelmässigi Reinigung', 'Flexible Termiin', 'Professionells Team', 'Qualitätsgarantie', 'Individuelli Aapassig'],
        en: ['Regular cleaning', 'Flexible schedules', 'Professional team', 'Quality guarantee', 'Individual customization'],
        it: ['Pulizia regolare', 'Orari flessibili', 'Team professionale', 'Garanzia di qualità', 'Personalizzazione individuale'],
        fr: ['Nettoyage régulier', 'Horaires flexibles', 'Équipe professionnelle', 'Garantie de qualité', 'Personnalisation individuelle']
      }
    };

    const serviceTranslations = {
      'general_cleaning': {
        de: { name: 'Allgemeine Hausreinigung', desc: '😌 Endlich wieder entspannt nach Hause kommen - Vollständiger Reinigungsservice für Ihr Zuhause' },
        'de-ch': { name: 'Putze vom Huus', desc: '😌 Endlich wieder entspannt hei cho - Vollständigs Putzservice für euis Huus' },
        en: { name: 'General House Cleaning', desc: '😌 Finally come home relaxed - Complete cleaning service for your house' },
        it: { name: 'Pulizia Generale della Casa', desc: '😌 Finalmente torna a casa rilassato - Servizio di pulizia completo per la tua casa' },
        fr: { name: 'Nettoyage Général de Maison', desc: '😌 Rentrez enfin détendu chez vous - Service de nettoyage complet pour votre maison' }
      },
      'apartment_cleaning': {
        de: { name: 'Allgemeine Wohnungsreinigung', desc: '✨ Das Gefühl eines perfekt sauberen Wohnumfeldes - Vollständiger Reinigungsservice für Wohnungen' },
        'de-ch': { name: 'Putze vo de Wohniig', desc: '✨ Es Gfühl vo perfekt saubere Wohnig - Vollständigs Putzservice für Wohnige' },
        en: { name: 'General Apartment Cleaning', desc: '✨ The feeling of a perfectly clean living environment - Complete cleaning service for apartments' },
        it: { name: 'Pulizia Generale Appartamento', desc: '✨ La sensazione di un ambiente perfettamente pulito - Servizio di pulizia completo per appartamenti' },
        fr: { name: 'Nettoyage Général d\'Appartement', desc: '✨ La sensation d\'un environnement parfaitement propre - Service de nettoyage complet pour appartements' }
      },
      'airbnb_management': {
        de: { name: 'AirBNB Management und Schlüssel', desc: '💰 Maximaler AirBNB Erfolg ohne Stress - Kompletter AirBNB Service mit Schlüsselverwaltung' },
        'de-ch': { name: 'AirBNB Verwautig und Schlüssel', desc: '💰 Maximale AirBNB Erfolg ohni Stress - Komplette AirBNB Service mit Schlüsselverwautig' },
        en: { name: 'AirBNB Management & Keys', desc: '💰 Maximum AirBNB success without stress - Complete AirBNB service with key management' },
        it: { name: 'Gestione AirBNB e Chiavi', desc: '💰 Massimo successo AirBNB senza stress - Servizio completo AirBNB con gestione chiavi' },
        fr: { name: 'Gestion AirBNB et Clés', desc: '💰 Succès maximum AirBNB sans stress - Service complet AirBNB avec gestion des clés' }
      },
      'business_cleaning': {
        de: { name: 'Geschäfts- und Büroreinigung', desc: '🏢 Professionelles Arbeitsumfeld für Erfolg - Professionelle Reinigung von Arbeitsplätzen' },
        'de-ch': { name: 'Putze vom Geschäft und Büro', desc: '🏢 Professionells Arbeitsumfäud für Erfolg - Professionells Putze vom Arbeitsplatz' },
        en: { name: 'Business & Office Cleaning', desc: '🏢 Professional work environment for success - Professional cleaning of workspaces' },
        it: { name: 'Pulizia Uffici e Aziende', desc: '🏢 Ambiente di lavoro professionale per il successo - Pulizia professionale degli spazi di lavoro' },
        fr: { name: 'Nettoyage de Bureaux et Entreprises', desc: '🏢 Environnement de travail professionnel pour le succès - Nettoyage professionnel des espaces de travail' }
      },
      'deep_cleaning': {
        de: { name: 'Tiefenreinigung für Hotels', desc: '⭐ 5-Sterne Gäste-Erfahrung garantiert - Tiefenreinigungsservice für Hotels und touristische Immobilien' },
        'de-ch': { name: 'Tüüf Putz für Hotels', desc: '⭐ 5-Stern Gäste-Erfahrig garantiert - Tüüfe Putz für Hotels und Tourismus-Immobilie' },
        en: { name: 'Deep Hotel Cleaning', desc: '⭐ 5-star guest experience guaranteed - Deep cleaning service for hotels and tourism properties' },
        it: { name: 'Pulizia Profonda per Hotel', desc: '⭐ Esperienza ospiti 5 stelle garantita - Servizio di pulizia profonda per hotel e proprietà turistiche' },
        fr: { name: 'Nettoyage en Profondeur pour Hôtels', desc: '⭐ Expérience client 5 étoiles garantie - Service de nettoyage en profondeur pour hôtels et propriétés touristiques' }
      },
      'building_maintenance': {
        de: { name: 'Hauswartung von Mehrfamilienhäusern', desc: '🏡 Werterhaltung Ihrer Immobilie - Professionelle Wartung für große Wohngebäude' },
        'de-ch': { name: 'Unterhalt vo Mehrfamiliehüüser', desc: '🏡 Werterhautig vo eui Immobilie - Professionelle Wartig för grossi Wohngebäude' },
        en: { name: 'Multi-Family Building Maintenance', desc: '🏡 Preserving your property value - Professional maintenance service for large residential buildings' },
        it: { name: 'Manutenzione Condomini', desc: '🏡 Preservare il valore della tua proprietà - Servizio di manutenzione professionale per grandi edifici residenziali' },
        fr: { name: 'Entretien d\'Immeubles Résidentiels', desc: '🏡 Préserver la valeur de votre propriété - Service d\'entretien professionnel pour grands bâtiments résidentiels' }
      },
      'rental_management': {
        de: { name: 'Wohnungen vermieten', desc: '🔑 Professionelle Vermietung - Komplettservice für Ihre Immobilienvermietung' },
        'de-ch': { name: 'Wohnige vermiete', desc: '🔑 Professionelli Vermietig - Komplettservice für eui Immobilie-Vermietig' },
        en: { name: 'Apartment Rental Management', desc: '🔑 Professional rental service - Complete service for your property rental' },
        it: { name: 'Gestione Affitti Appartamenti', desc: '🔑 Servizio affitto professionale - Servizio completo per il tuo affitto immobiliare' },
        fr: { name: 'Gestion Location Appartements', desc: '🔑 Service location professionnel - Service complet pour votre location immobilière' }
      },
      'park_maintenance': {
        de: { name: 'Unterhalt von Parks und Grünflächen', desc: '🌿 Professionelle Grünpflege - Pflege und Wartung Ihrer Außenanlagen' },
        'de-ch': { name: 'Unterhalt vo Pärk und Grüefläche', desc: '🌿 Professionelli Grüepfleg - Pfleg und Wartig vo eune Ussenaalaage' },
        en: { name: 'Park and Green Space Maintenance', desc: '🌿 Professional landscaping - Care and maintenance of your outdoor facilities' },
        it: { name: 'Manutenzione Parchi e Aree Verdi', desc: '🌿 Manutenzione professionale - Cura e manutenzione delle vostre aree esterne' },
        fr: { name: 'Entretien Parcs et Espaces Verts', desc: '🌿 Entretien professionnel - Soins et entretien de vos espaces extérieurs' }
      },
      'painting_services': {
        de: { name: 'Malerarbeiten', desc: '🎨 Professionelle Malerdienstleistungen - Hochwertige Anstriche für Innen und Außen' },
        'de-ch': { name: 'Malerarbete', desc: '🎨 Professionelli Malerdienschtleistige - Hochwertigi Aastriche für Inne und Usse' },
        en: { name: 'Painting Services', desc: '🎨 Professional painting services - High-quality finishes for interior and exterior' },
        it: { name: 'Servizi di Pittura', desc: '🎨 Servizi di pittura professionali - Finiture di alta qualità per interni ed esterni' },
        fr: { name: 'Services de Peinture', desc: '🎨 Services de peinture professionnels - Finitions de haute qualité pour intérieur et extérieur' }
      },
      'maintenance_cleaning': {
        de: { name: 'Unterhaltsreinigung', desc: '🧹 Regelmässige Unterhaltsreinigung für Wohnungen - Auf Anfrage' },
        'de-ch': { name: 'Unterhaltsreinigung', desc: '🧹 Regelmässigi Unterhaltsreinigung für Wohnige - Uf Afrag' },
        en: { name: 'Maintenance Cleaning', desc: '🧹 Regular maintenance cleaning for apartments - On Request' },
        it: { name: 'Pulizia di Manutenzione', desc: '🧹 Pulizia di manutenzione regolare per appartamenti - Su Richiesta' },
        fr: { name: 'Nettoyage d\'Entretien', desc: '🧹 Nettoyage d\'entretien régulier pour appartements - Sur Demande' }
      }
    };

    return baseServices.map(service => {
      const translation = serviceTranslations[service.id]?.[language] || serviceTranslations[service.id]?.de;
      const features = serviceFeatures[service.id]?.[language] || serviceFeatures[service.id]?.de;
      const pricing = getMarketPricing(service.id, service.duration);
      
      return {
        ...service,
        name: translation.name,
        description: translation.desc,
        features: features,
        price: pricing.price,
        currency: pricing.currency
      };
    });
  }, [language]); // Re-create when language changes

  const localizedServices = getLocalizedServices();

  // Update current service when language changes
  React.useEffect(() => {
    if (currentService) {
      const updatedService = localizedServices.find(s => s.id === currentService.id);
      if (updatedService) {
        setCurrentService(updatedService);
      }
    }
  }, [language, currentService?.id]);

  // Force re-render when language context changes
  React.useEffect(() => {
    // This effect ensures component re-renders when language changes
  }, [language]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || 
        !formData.address || !formData.preferred_date || !formData.preferred_time || !currentService) {
      toast({
        title: t('booking.error', 'Gabim'),
        description: t('booking.errorDesc', 'Ju lutemi plotësoni të gjitha fushat e kërkuara dhe zgjidhni një shërbim.'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const bookingData = {
        ...formData,
        service_type: currentService.id,
        preferred_date: format(formData.preferred_date, 'yyyy-MM-dd')
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;
      
      // If payment method is card or twint, redirect to Stripe checkout
      if (formData.payment_method === 'card' || formData.payment_method === 'twint') {
        try {
          const checkoutResponse = await axios.post(`${API}/payments/checkout/session`, {
            booking_id: booking.id,
            origin_url: window.location.origin,
            payment_method: formData.payment_method
          });
          
          // Redirect to Stripe checkout
          if (checkoutResponse.data.url) {
            window.location.href = checkoutResponse.data.url;
            return; // Don't show toast or reset form as user is being redirected
          }
        } catch (checkoutError) {
          console.error('Checkout error:', checkoutError);
          toast({
            title: t('booking.error', 'Gabim'),
            description: t('booking.paymentError', 'Dështoi në krijimin e sesionit të pagesës. Ju lutemi provoni përsëri.'),
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }
      
      // For cash payment, show success message
      toast({
        title: t('booking.success', 'Rezervimi u Konfirmua!'),
        description: t('booking.successDesc', 'Rezervimi juaj është dërguar. Do t\'ju kontaktojmë së shpejti për të konfirmuar takimin.'),
      });
      
      // Reset form
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        address: '',
        preferred_date: null,
        preferred_time: '',
        special_requirements: '',
        payment_method: 'card'
      });
      
      // Reset service selection if it wasn't pre-selected
      if (!selectedService) {
        setCurrentService(null);
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: t('booking.error', 'Gabim'),
        description: t('booking.errorServer', 'Dështoi në krijimin e rezervimit. Ju lutemi provoni përsëri.'),
        variant: "destructive",
      });
    }
    
    setLoading(false);
  };

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-teal-50"></div>
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1742483359033-13315b247c74')] bg-cover bg-center opacity-10"></div>
      
      <div className="relative z-10 container mx-auto px-6 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Header Section - Same as Hero */}
          <Badge className="mb-6 bg-emerald-100 text-emerald-800 border-emerald-200 px-4 py-2 text-sm font-medium">
            {t('booking.badge', 'Rezervo Shërbimin Tuaj')}
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-2 leading-tight">
            <span className="bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
              Brillanz & Glanz
            </span>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl font-semibold text-emerald-700 mb-4">
            {t('hero.slogan', 'Leben mit Exzellenz')}
          </p>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 max-w-2xl mx-auto px-2">
            {t('booking.subtitle', 'Zgjidhni shërbimin dhe plotësoni të dhënat tuaja për një rezervim të shpejtë dhe profesional.')}
          </p>
          

          
          <Button 
            variant="outline" 
            onClick={onBack}
            className="mb-8 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-6 py-2 rounded-lg transition-all duration-300"
          >
            ← {t('booking.backBtn', 'Kthehu tek Faqja Kryesore')}
          </Button>

          {/* Service Selection - Integrated like Hero */}
          {!currentService ? (
            <>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 px-2">{t('booking.selectService', 'Zgjidhni Shërbimin Tuaj')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 px-2">
                {localizedServices.slice(0, 7).map((service, index) => (
                  <div 
                    key={`${service.id}-${index}`}
                    data-service-id={service.id}
                    onClick={() => setCurrentService(service)}
                    className="service-card bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg cursor-pointer hover:shadow-xl hover:bg-white/90 transition-all duration-300 transform hover:-translate-y-2"
                    role="button"
                    tabIndex={0}
                  >
                    <img src={service.image} alt={service.name} className="w-full h-32 sm:h-40 object-cover rounded-xl mb-3 sm:mb-4" />
                    <h4 className="font-bold text-gray-900 text-base sm:text-lg mb-2 leading-tight">{service.name}</h4>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 line-clamp-2">{service.description}</p>
                    <div className="flex items-center justify-between flex-wrap gap-2">

                      <span className="text-xl sm:text-2xl font-bold text-emerald-600">{service.currency}{service.price}</span>
                      <div className="flex items-center text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        <span className="text-xs sm:text-sm">{getDurationText(service.duration, language)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Selected Service Display - Hero Style */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  <img 
                    src={currentService.image} 
                    alt={currentService.name}
                    className="w-full sm:w-24 h-40 sm:h-24 rounded-xl object-cover"
                  />
                  <div className="flex-1 text-left w-full">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">{currentService.name}</h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-3">{currentService.description}</p>
                    
                    {/* Service Features */}
                    {currentService.features && currentService.features.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">{t('service.includes', 'Përfshihet në shërbim')}:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {currentService.features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                              <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      <span className="text-xl sm:text-2xl font-bold text-emerald-600">{currentService.currency}{currentService.price}</span>
                      <div className="flex items-center text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        <span className="text-sm">{getDurationText(currentService.duration, language)}</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCurrentService(null)}
                    className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-3 sm:px-4 py-2 rounded-lg text-sm w-full sm:w-auto mt-2 sm:mt-0"
                  >
                    {t('booking.changeService', 'Ndrysho Shërbimin')}
                  </Button>
                </div>
              </div>

              {/* Booking Form - Integrated in Hero Style */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">{t('booking.fillData', 'Plotësoni të Dhënat Tuaja')}</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {!currentService && (
                    <div className="mb-6 p-4 bg-yellow-50/80 backdrop-blur-sm border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800 font-medium text-center">
                        {t('booking.selectServiceFirst', 'Ju lutemi zgjidhni një shërbim më sipër për të vazhduar me rezervimin.')}
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="customer_name">{t('booking.fullName', 'Emri i Plotë')} *</Label>
                      <Input
                        id="customer_name"
                        value={formData.customer_name}
                        onChange={(e) => handleInputChange('customer_name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_email">{t('booking.email', 'Adresa elektronike')} *</Label>
                      <Input
                        id="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => handleInputChange('customer_email', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="customer_phone">{t('booking.phone', 'Telefoni')} *</Label>
                      <Input
                        id="customer_phone"
                        value={formData.customer_phone}
                        onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferred_time">{t('booking.preferredTime', 'Ora e Preferuar')} *</Label>
                      <Select value={formData.preferred_time} onValueChange={(value) => handleInputChange('preferred_time', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('booking.selectTime', 'Zgjidhni orën')} />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="address">{t('booking.address', 'Adresa')} *</Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder={t('booking.addressPlaceholder', 'Shkruani adresën e plotë të vendbanimit')}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>{t('booking.preferredDate', 'Data e Preferuar')} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left font-normal ${
                            !formData.preferred_date && "text-muted-foreground"
                          }`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.preferred_date ? format(formData.preferred_date, "PPP") : t('booking.selectDate', 'Zgjidhni datën')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.preferred_date}
                          onSelect={(date) => handleInputChange('preferred_date', date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div>
                    <Label htmlFor="special_requirements">{t('booking.specialRequirements', 'Kërkesat Speciale')}</Label>
                    <Textarea
                      id="special_requirements"
                      value={formData.special_requirements}
                      onChange={(e) => handleInputChange('special_requirements', e.target.value)}
                      placeholder={t('booking.specialRequirementsPlaceholder', 'Nëse keni ndonjë kërkesë speciale, na e tregoni këtu...')}
                    />
                  </div>
                  
                  <div>
                    <Label>{t('booking.paymentMethod', 'Mënyra e Pagesës')}</Label>
                    <Select value={formData.payment_method} onValueChange={(value) => handleInputChange('payment_method', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('booking.selectPaymentMethod', 'Zgjidhni mënyrën e pagesës')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="card">{t('booking.card', 'Pagesa me Kartë')}</SelectItem>
                        <SelectItem value="twint">{t('booking.twint', 'TWINT')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transition-all duration-300 disabled:opacity-50 min-h-[44px]"
                    disabled={loading || !currentService}
                  >
                    {loading ? t('booking.submitting', 'Duke Rezervuar...') : t('booking.bookNow', 'Rezervo Tani')}
                  </Button>
                </form>
              </div>
            </>
          )}

          {/* Call to Action Buttons - Always Visible */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {t('cta.alternative', 'Oder kontaktieren Sie uns direkt')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('cta.description', 'Haben Sie Fragen? Kontaktieren Sie uns über Email oder WhatsApp')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto mb-8">
              {/* Email Button */}
              <Button
                onClick={() => window.open('mailto:thaqi.kujtim@gmx.net', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 font-semibold rounded-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 min-h-[44px]"
              >
                <Mail className="h-5 w-5" />
                E-MAIL
              </Button>
              
              {/* WhatsApp Button */}
              <Button
                onClick={() => {
                  const message = encodeURIComponent(t('whatsapp.contactMessage', 'Hallo! Ich möchte mehr über Ihre Reinigungsdienstleistungen erfahren.'));
                  window.open(`https://wa.me/41767669430?text=${message}`, '_blank');
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-4 font-semibold rounded-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 min-h-[44px]"
              >
                <MessageCircle className="h-5 w-5" />
                WHATSAPP
              </Button>
            </div>
          </div>


        </div>
      </div>
    </section>
  );
};

// Referral Program Pop-up Component removed as requested

// Main App Content with navigation
const MainAppContent = () => {
  const [currentView, setCurrentView] = useState('hero'); 
  const [selectedService, setSelectedService] = useState(null);
  const [partnerId, setPartnerId] = useState(null);
  
  // Referral Program Pop-up removed

  const handleBookNow = () => {
    setCurrentView('booking');
  };

  const handleLearnMore = () => {
    setCurrentView('learn-more-combined');
  };

  const handlePartnerProgram = () => {
    setCurrentView('partnerHero');
  };

  const handlePartnerLogin = () => {
    setCurrentView('partnerLogin');
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setCurrentView('booking');
  };

  const handleJoinProgram = () => {
    setCurrentView('partnerRegistration');
  };

  const handlePartnerLoginSuccess = (id) => {
    setPartnerId(id);
    setCurrentView('partnerDashboard');
  };

  const handleBack = () => {
    setCurrentView('hero');
    setPartnerId(null);
    setSelectedService(null);
  };

  const handleBackToPartnerHero = () => {
    setCurrentView('partnerHero');
  };

  const handlePrivacyClick = () => {
    setCurrentView('privacy');
  };

  const handleImprintClick = () => {
    setCurrentView('imprint');
  };

  return (
    <div className="App">
      {/* Fixed Language Selector */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector />
      </div>

      {currentView === 'hero' && (
        <>
          <Hero 
            onBookNow={handleBookNow} 
            onLearnMore={handleLearnMore}
            onPartnerProgram={handlePartnerProgram}
            onPartnerLogin={handlePartnerLogin}
          />
        </>
      )}

      {currentView === 'learn-more-combined' && (
        <LearnMoreCombined 
          onBack={handleBack}
          onJoinProgram={handleJoinProgram}
          onPartnerLogin={handlePartnerLogin}
          onBookNow={handleBookNow}
        />
      )}

      {currentView === 'booking' && (
        <BookingFormWrapper 
          selectedService={selectedService} 
          onBack={handleBack}
        />
      )}

      {currentView === 'partnerLogin' && (
        <PartnerLogin 
          onBack={handleBack}
          onLoginSuccess={handlePartnerLoginSuccess}
        />
      )}

      {currentView === 'partnerRegistration' && (
        <SimplePartnerForm 
          onBack={handleBackToPartnerHero}
        />
      )}

      {currentView === 'partnerDashboard' && partnerId && (
        <EnhancedPartnerDashboard 
          partnerId={partnerId}
          onBack={handleBack}
        />
      )}

      {currentView === 'privacy' && (
        <Privacy onBack={handleBack} />
      )}

      {currentView === 'imprint' && (
        <Imprint onBack={handleBack} />
      )}
      
      {/* Footer - Show on all pages except dashboard */}
      {currentView !== 'partnerDashboard' && (
        <Footer 
          onPrivacyClick={handlePrivacyClick}
          onImprintClick={handleImprintClick}
        />
      )}
      
      {/* PWA Install Prompt */}
      <InstallPWA />
      
      {/* Referral Program Pop-up removed */}
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-gray-50">
        <div className="fixed top-4 right-4 z-50">
          <LanguageSelector />
        </div>
        <MainAppContent />
        <Toaster />
      </div>
    </LanguageProvider>
  );
}

export default App;